import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { moduleService } from '../../services'
import { useAuth } from '../../auth/auth.context'
import PageLayout from '../../components/layout/page-layout'
import { api } from '../../lib/api'
import { Container } from '../../components/ui/container'
import { PageHeader } from '../../components/layout/page-header'
import { buttonVariants } from '../../components/ui/button-variants'
import { LANGUAGES, LanguageCode } from '../../lib/constants/languages'

interface QuizQuestion {
  question: string
  options: string[]
  correctAnswer: number
  explanation?: string
}

interface ModuleFormData {
  isActive: boolean
  defaultLanguage: string
  translations: Record<
    string,
    {
      title: string
      description: string
    }
  >
}

interface FormErrors {
  translations?: Record<string, { title?: string }>
  chapters?: Record<number, { translations?: Record<string, { title?: string }> }>
  global?: string
  languageSelection?: string
  chapterQuestionIssues?: Record<number, Record<string, string[]>>
}

type ChapterForm = {
  order: number
  videoUrl?: string
  imageUrl?: string
  duration?: number
  translations: Record<
    string,
    {
      title: string
      description: string
    }
  >
  questions: Record<string, QuizQuestion[]>
}

export default function ModuleFormPage({
  moduleType = 'onboarding',
}: { moduleType?: 'onboarding' | 'weiterbildung' } = {}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: routeId } = useParams()
  // "new" is the create path, never a module id.
  const id = routeId && routeId !== 'new' ? routeId : undefined
  const queryClient = useQueryClient()
  const { company, user } = useAuth()
  const isEditing = !!id

  const backPath = moduleType === 'weiterbildung' ? '/weiterbildung' : '/modules'
  const typeLabel =
    moduleType === 'weiterbildung' ? t('common.typeWeiterbildung') : t('common.typeOnboarding')
  const typeArticle =
    moduleType === 'weiterbildung' || moduleType === 'onboarding'
      ? t('common.articleNewFeminine')
      : t('common.articleNewMasculine')
  const typeDefArticle =
    moduleType === 'weiterbildung' || moduleType === 'onboarding'
      ? t('common.articleDefFeminine')
      : t('common.articleDefNeuter')
  const typeI18n = { type: typeLabel, typeArticle: typeDefArticle }

  const [formData, setFormData] = useState<ModuleFormData>({
    isActive: false,
    defaultLanguage: 'de',
    translations: {
      de: {
        title: '',
        description: '',
      },
    },
  })

  const [selectedLanguages, setSelectedLanguages] = useState<LanguageCode[]>(['de'])
  const [activeLanguageTab, setActiveLanguageTab] = useState<LanguageCode>('de')
  const [showLanguagePicker, setShowLanguagePicker] = useState(false)

  // Chapters state
  const [chapters, setChapters] = useState<ChapterForm[]>([])
  const [activeChapterIndex, setActiveChapterIndex] = useState<number>(-1)

  const [newQuestion, setNewQuestion] = useState<QuizQuestion>({
    question: '',
    options: ['', ''], // Start with 2 options instead of 4
    correctAnswer: 0,
    explanation: '',
  })

  const [showQuestionForm, setShowQuestionForm] = useState(false)
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null)
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [uploadingVideo, setUploadingVideo] = useState<number | null>(null) // chapter index that's uploading
  const [uploadingImage, setUploadingImage] = useState<number | null>(null) // chapter index that's uploading

  // Load module data for editing
  const { data: moduleData } = useQuery({
    queryKey: ['module', id],
    queryFn: () => moduleService.getModuleById(id!),
    enabled: !!id,
  })

  // Update form data when module data loads
  useEffect(() => {
    if (moduleData) {
      const translations = (moduleData as any).translations || {}
      const defaultLang = (moduleData as any).defaultLanguage || 'de'
      const availableLangs = Object.keys(translations).filter((c): c is LanguageCode =>
        LANGUAGES.some(l => l.code === c)
      )

      // Ensure default language is included
      const finalSelectedLangs: LanguageCode[] =
        availableLangs.length > 0
          ? availableLangs.includes(defaultLang as LanguageCode)
            ? availableLangs
            : [...availableLangs, defaultLang as LanguageCode]
          : [defaultLang as LanguageCode]

      // Ensure all selected languages have translation objects
      const finalTranslations: typeof translations = { ...translations }
      for (const lang of finalSelectedLangs) {
        if (!finalTranslations[lang]) {
          finalTranslations[lang] = {
            title: '',
            description: '',
          }
        }
      }

      setFormData({
        isActive: moduleData.isActive,
        defaultLanguage: (moduleData as any).defaultLanguage || 'de',
        translations: finalTranslations,
      })

      // Prefill chapters if present
      const existingChapters = (moduleData as any)?.chapters || []
      if (Array.isArray(existingChapters) && existingChapters.length > 0) {
        const mapped: ChapterForm[] = existingChapters
          .slice()
          .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
          .map((ch: any) => ({
            order: ch.order ?? 0,
            videoUrl: ch.videoUrl,
            imageUrl: ch.imageUrl,
            duration: ch.duration,
            translations: (ch.translations as any) || {},
            questions: (ch.questionsByLanguage as any) || {},
          }))
        setChapters(mapped)
        setActiveChapterIndex(0)
      }

      setSelectedLanguages(finalSelectedLangs)
      setActiveLanguageTab(finalSelectedLangs[0])
    }
  }, [moduleData])

  const createModuleMutation = useMutation({
    mutationFn: (data: any) => moduleService.createModule(data),
    onSuccess: created => {
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.invalidateQueries({ queryKey: ['weiterbildung-modules'] })
      const createdId = created?.id
      if (!createdId) {
        navigate(backPath)
        return
      }
      // After first save, stay in edit mode so chapters can be added and module can be published
      navigate(`${backPath}/${createdId}/edit`)
    },
    onError: (err: any) => {
      console.error('Create module error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.userMessage ||
        err?.message ||
        t('errors.createModule', { type: typeLabel })
      setError(errorMessage)
      setLoading(false)
    },
  })

  const updateModuleMutation = useMutation({
    mutationFn: (data: any) => moduleService.updateModule(id!, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.invalidateQueries({ queryKey: ['weiterbildung-modules'] })
      queryClient.invalidateQueries({ queryKey: ['module', id] })
      // Stay on edit page after publish/save so user can continue editing chapters
      if (variables?.isActive) {
        setFormData(prev => ({ ...prev, isActive: true }))
      }
      setLoading(false)
    },
    onError: (err: any) => {
      console.error('Update module error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.userMessage ||
        err?.message ||
        t('errors.updateModule', { type: typeLabel })
      setError(errorMessage)
      setLoading(false)
    },
  })

  const deleteModuleMutation = useMutation({
    mutationFn: () => moduleService.deleteModule(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.invalidateQueries({ queryKey: ['weiterbildung-modules'] })
      navigate(backPath)
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || t('errors.deleteModule', typeI18n))
    },
  })

  const uploadChapterMedia = async (
    file: File,
    chapterIndex: number,
    mediaType: 'video' | 'image'
  ) => {
    if (!file) return

    const allowedTypes =
      mediaType === 'video'
        ? ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo']
        : ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']

    if (!allowedTypes.includes(file.type)) {
      setError(t('common.invalidFileType', { types: allowedTypes.join(', ') }))
      return
    }

    // File size limit: videos up to 500MB, images up to 25MB.
    const maxSize = mediaType === 'video' ? 500 * 1024 * 1024 : 25 * 1024 * 1024
    if (file.size > maxSize) {
      setError(
        mediaType === 'video' ? t('common.fileTooLarge500mb') : t('common.fileTooLarge25mb')
      )
      return
    }

    if (mediaType === 'video') {
      setUploadingVideo(chapterIndex)
    } else {
      setUploadingImage(chapterIndex)
    }

    setError(undefined)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/modules/upload-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const payload = response.data.data || response.data
      const videoUrl = payload?.videoUrl || undefined
      const imageUrl = payload?.imageUrl || undefined
      const duration = typeof payload?.duration === 'number' ? payload.duration : undefined

      // Persist whichever media URL was returned by backend for this chapter.
      setChapters(prev =>
        prev.map((c, i) => {
          if (i !== chapterIndex) return c
          return {
            ...c,
            videoUrl: videoUrl || c.videoUrl,
            imageUrl: imageUrl || c.imageUrl,
            duration: duration || c.duration,
          }
        })
      )
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.userMessage ||
        err?.message ||
        t('errors.uploadMedia', {
          mediaType: t(
            mediaType === 'video' ? 'errors.uploadMediaVideo' : 'errors.uploadMediaImage'
          ),
        })
      setError(errorMessage)
    } finally {
      if (mediaType === 'video') {
        setUploadingVideo(null)
      } else {
        setUploadingImage(null)
      }
    }
  }

  const handleVideoUpload = async (file: File, chapterIndex: number) => {
    await uploadChapterMedia(file, chapterIndex, 'video')
  }

  const handleImageUpload = async (file: File, chapterIndex: number) => {
    await uploadChapterMedia(file, chapterIndex, 'image')
  }

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleSubmit = async (
    e?: React.FormEvent,
    options?: { activate?: boolean }
  ) => {
    e?.preventDefault()
    setError(undefined)
    setLoading(true)
    setFormErrors({})

    // Validate companyId is available
    const companyId = isEditing ? moduleData?.companyId : (company?.id ?? user?.companyId)
    if (!companyId) {
      setError(t('common.companyIdUnavailable'))
      setLoading(false)
      return
    }

    // Create always starts inactive; publish sets active
    const nextIsActive = !isEditing
      ? false
      : options?.activate
        ? true
        : formData.isActive

    try {
      // Client-side validation
      const newErrors: FormErrors = {}
      if (selectedLanguages.length === 0) {
        newErrors.languageSelection = t('errors.selectAtLeastOneLanguage')
      }

      // Validate module translation titles
      for (const lang of selectedLanguages) {
        const translation = formData.translations[lang]
        if (!translation || !translation.title || !translation.title.trim()) {
          if (!newErrors.translations) newErrors.translations = {}
          newErrors.translations[lang] = { title: t('errors.titleRequired') }
        }
      }

      // Validate chapter translation titles if chapters present
      if (chapters.length > 0) {
        for (let i = 0; i < chapters.length; i++) {
          const ch = chapters[i]
          for (const lang of selectedLanguages) {
            const ct = ch.translations[lang]
            if (!ct || !ct.title || ct.title.trim().length === 0) {
              if (!newErrors.chapters) newErrors.chapters = {}
              if (!newErrors.chapters[i]) newErrors.chapters[i] = { translations: {} }
              newErrors.chapters[i].translations![lang] = {
                title: t('errors.chapterTitleRequired'),
              }
            }
          }
        }
      }

      // Chapter quiz question validation summary
      if (chapters.length > 0) {
        const chapterQuestionIssues: Record<number, Record<string, string[]>> = {}
        chapters.forEach((ch, cIdx) => {
          selectedLanguages.forEach(lang => {
            const list = ch.questions[lang] || []
            list.forEach((q, idx) => {
              const errs: string[] = []
              if (!q.question.trim()) {
                errs.push(
                  t('errors.questionTextMissing', { chapter: ch.order, question: idx + 1 })
                )
              }
              const filled = q.options.filter(o => o.trim())
              if (filled.length < 2) {
                errs.push(
                  t('errors.questionMinOptions', { chapter: ch.order, question: idx + 1 })
                )
              }
              if (q.correctAnswer < 0 || q.correctAnswer >= filled.length) {
                errs.push(
                  t('errors.questionInvalidAnswer', { chapter: ch.order, question: idx + 1 })
                )
              }
              if (errs.length) {
                if (!chapterQuestionIssues[cIdx]) chapterQuestionIssues[cIdx] = {}
                if (!chapterQuestionIssues[cIdx][lang]) chapterQuestionIssues[cIdx][lang] = []
                chapterQuestionIssues[cIdx][lang].push(...errs)
              }
            })
          })
        })
        if (Object.keys(chapterQuestionIssues).length)
          newErrors.chapterQuestionIssues = chapterQuestionIssues
      }

      if (Object.keys(newErrors).length > 0) {
        console.log('New errors:', newErrors)
        setFormErrors(newErrors)
        setLoading(false)
        return
      }

      // If chapters exist, build CreateModuleWithChaptersDto
      if (chapters.length > 0) {
        const chaptersDto = chapters
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(ch => ({
            order: ch.order,
            videoUrl: ch.videoUrl || undefined,
            imageUrl: ch.imageUrl || undefined,
            duration: ch.duration || undefined,
            translations: Object.fromEntries(
              Object.entries(ch.translations || {})
                .filter(([lang]) => selectedLanguages.includes(lang as LanguageCode))
                .map(([lang, t]) => [lang, { title: t.title, description: t.description }])
            ),
            questions: Object.fromEntries(
              Object.entries(ch.questions || {})
                .filter(([lang]) => selectedLanguages.includes(lang as LanguageCode))
                .map(([lang, qs]) => [
                  lang,
                  qs.map(q => ({
                    question: q.question,
                    options: q.options,
                    correctAnswer: q.correctAnswer,
                    explanation: q.explanation || undefined,
                  })),
                ])
            ),
          }))

        // Filter translations to only include selected languages
        const filteredTranslations = Object.fromEntries(
          Object.entries(formData.translations).filter(([lang]) =>
            selectedLanguages.includes(lang as LanguageCode)
          )
        )

        const submitData = {
          companyId,
          type: moduleType,
          isActive: nextIsActive,
          defaultLanguage: formData.defaultLanguage,
          translations: filteredTranslations,
          chapters: chaptersDto,
        } as any

        console.log('Submitting module with chapters:', submitData)

        if (isEditing) {
          await updateModuleMutation.mutateAsync(submitData)
        } else {
          await createModuleMutation.mutateAsync(submitData)
        }
      } else {
        // Legacy path without chapters
        // Filter translations to only include selected languages
        const filteredTranslations = Object.fromEntries(
          Object.entries(formData.translations).filter(([lang]) =>
            selectedLanguages.includes(lang as LanguageCode)
          )
        )

        const submitData = {
          isActive: nextIsActive,
          companyId,
          type: moduleType,
          defaultLanguage: formData.defaultLanguage,
          translations: filteredTranslations,
        }

        console.log('Submitting legacy module data:', submitData)
        console.log('Translations:', Object.keys(submitData.translations))

        if (isEditing) {
          await updateModuleMutation.mutateAsync(submitData)
        } else {
          await createModuleMutation.mutateAsync(submitData)
        }
      }
    } catch (error) {
      // Error is handled by the mutation
      console.error('Error submitting module:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTranslationChange = (language: LanguageCode, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: {
          ...prev.translations[language],
          [field]: value,
        },
      },
    }))
    // Clear field error when user types
    if (formErrors.translations?.[language]?.title && field === 'title' && value.trim()) {
      setFormErrors(prev => {
        const next = { ...prev }
        if (next.translations?.[language]) delete next.translations[language].title
        return next
      })
    }
  }

  // Chapter translation change handler (avoids state mutation during render)
  const handleChapterTitleChange = (
    chapterIndex: number,
    language: LanguageCode,
    value: string
  ) => {
    setChapters(prev =>
      prev.map((c, i) =>
        i === chapterIndex
          ? {
              ...c,
              translations: {
                ...c.translations,
                [language]: {
                  ...(c.translations[language] || { title: '', description: '' }),
                  title: value,
                },
              },
            }
          : c
      )
    )
    // Clear chapter title error when user types
    if (formErrors.chapters?.[chapterIndex]?.translations?.[language]?.title && value.trim()) {
      setFormErrors(prev => {
        const next = { ...prev }
        const chapterErr = next.chapters?.[chapterIndex]?.translations?.[language]
        if (chapterErr?.title) delete chapterErr.title
        return next
      })
    }
  }

  const handleLanguageToggle = (languageCode: LanguageCode) => {
    if (selectedLanguages.includes(languageCode)) {
      // Prevent removing the default language
      if (languageCode === formData.defaultLanguage) {
        setError(t('errors.cannotRemoveDefaultLanguage'))
        return
      }

      // Remove language
      const newLanguages = selectedLanguages.filter(lang => lang !== languageCode)
      setSelectedLanguages(newLanguages)

      // Remove translation data
      setFormData(prev => {
        const newTranslations = { ...prev.translations }
        delete newTranslations[languageCode]
        return {
          ...prev,
          translations: newTranslations,
        }
      })

      // Remove language from all chapters' translations and questions
      setChapters(prev =>
        prev.map(ch => {
          const newTranslations = { ...ch.translations }
          delete newTranslations[languageCode]

          const newQuestions = { ...ch.questions }
          delete newQuestions[languageCode]

          return {
            ...ch,
            translations: newTranslations,
            questions: newQuestions,
          }
        })
      )

      // Switch to another tab if current tab is being removed
      if (activeLanguageTab === languageCode && newLanguages.length > 0) {
        setActiveLanguageTab(newLanguages[0])
      }
    } else {
      // Add language
      const newLanguages = [...selectedLanguages, languageCode]
      setSelectedLanguages(newLanguages)

      // Add empty translation data
      setFormData(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [languageCode]: {
            title: '',
            description: '',
          },
        },
      }))

      // Ensure chapters have translation/question slots for the new language
      setChapters(prev =>
        prev.map(ch => ({
          ...ch,
          translations: {
            ...ch.translations,
            [languageCode]: ch.translations[languageCode] || { title: '', description: '' },
          },
          questions: {
            ...ch.questions,
            [languageCode]: ch.questions[languageCode] || [],
          },
        }))
      )

      // Switch to the new language tab
      setActiveLanguageTab(languageCode)
    }
  }

  const handleAddLanguage = (languageCode: LanguageCode) => {
    if (!selectedLanguages.includes(languageCode)) {
      handleLanguageToggle(languageCode)
    }
    setShowLanguagePicker(false)
  }

  const languagesAvailableToAdd = LANGUAGES.filter(l => !selectedLanguages.includes(l.code))

  const isBusy = loading || createModuleMutation.isPending || updateModuleMutation.isPending
  const showChapters = isEditing

  const handleNewQuestionChange = (field: keyof QuizQuestion, value: any) => {
    setNewQuestion(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleNewQuestionOptionChange = (index: number, value: string) => {
    setNewQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => (i === index ? value : opt)),
    }))
  }

  const handleAddOption = () => {
    setNewQuestion(prev => ({
      ...prev,
      options: [...prev.options, ''],
    }))
  }

  const handleRemoveOption = (index: number) => {
    if (newQuestion.options.length > 2) {
      setNewQuestion(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index),
        correctAnswer:
          prev.correctAnswer >= index ? Math.max(0, prev.correctAnswer - 1) : prev.correctAnswer,
      }))
    }
  }

  const pageTitle = isEditing
    ? t('common.editType', { type: typeLabel })
    : t('common.createNewType', { type: typeLabel })
  const pageSubtitle = isEditing
    ? t('common.editModuleSubtitle', typeI18n)
    : t('common.createTypeSubtitle', { article: typeArticle, type: typeLabel })

  return (
    <PageLayout>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-neutral-950/25 backdrop-blur-sm flex items-center justify-center z-50'>
          <div className='highlight-card animate-scale-in rounded-[var(--radius-xl)] p-6 max-w-md w-full mx-4'>
            <h3 className='text-lg font-semibold text-neutral-900 mb-4'>
              {t('common.deleteModuleTitle', typeI18n)}
            </h3>
            <p className='text-neutral-600 mb-6'>{t('common.deleteModuleMessage', typeI18n)}</p>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className='btn-secondary-modern px-4 py-2 rounded-lg font-medium'
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  deleteModuleMutation.mutate()
                  setShowDeleteConfirm(false)
                }}
                disabled={deleteModuleMutation.isPending}
                className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-neutral-400 disabled:cursor-not-allowed transition-colors'
              >
                {deleteModuleMutation.isPending ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
      <Container>
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          action={
            <Link to={backPath} className={buttonVariants({ variant: 'secondary', size: 'md' })}>
              {t('common.backToOverview')}
            </Link>
          }
        />
        <form onSubmit={e => void handleSubmit(e)} className='space-y-8'>
          {/* Error Message */}
          {error && (
            <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
              <p className='text-red-600 '>{error}</p>
            </div>
          )}

          {/* Comprehensive Validation Errors Summary */}
          {Object.keys(formErrors).length > 0 && (
            <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
              <h3 className='text-lg font-semibold text-red-700 mb-3'>
                {t('common.fixErrorsTitle')}
              </h3>
              <div className='space-y-4'>
                {/* Language Selection Error */}
                {formErrors.languageSelection && (
                  <div className='p-3 bg-red-100 rounded border border-red-300 '>
                    <p className='text-sm font-medium text-red-700 '>{t('common.languagesSection')}</p>
                    <p className='text-sm text-red-600 '>{formErrors.languageSelection}</p>
                  </div>
                )}

                {/* Module Translation Errors */}
                {formErrors.translations && Object.keys(formErrors.translations).length > 0 && (
                  <div className='p-3 bg-red-100 rounded border border-red-300 '>
                    <p className='text-sm font-medium text-red-700 mb-2'>
                      {t('common.moduleTranslations', typeI18n)}
                    </p>
                    <ul className='list-disc list-inside space-y-1'>
                      {Object.entries(formErrors.translations).map(([lang, errors]) => {
                        const language = LANGUAGES.find(l => l.code === lang)
                        return (
                          <li key={lang} className='text-sm text-red-600 '>
                            <span className='font-medium'>
                              {language?.flag} {language?.name}:
                            </span>{' '}
                            {errors.title}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}

                {/* Chapter Translation Errors */}
                {formErrors.chapters && Object.keys(formErrors.chapters).length > 0 && (
                  <div className='p-3 bg-red-100 rounded border border-red-300 '>
                    <p className='text-sm font-medium text-red-700 mb-2'>
                      {t('common.chapterTranslations')}
                    </p>
                    <div className='space-y-2'>
                      {Object.entries(formErrors.chapters).map(([chapterIdx, chapterErrors]) => {
                        const chapterIndex = parseInt(chapterIdx, 10)
                        const chapter = chapters[chapterIndex]
                        const chapterOrder = chapter?.order || chapterIndex + 1
                        return (
                          <div key={chapterIdx} className='ml-2'>
                            <button
                              type='button'
                              onClick={() => {
                                setActiveChapterIndex(chapterIndex)
                                // Also switch to the first language with an error if available
                                const firstErrorLang = chapterErrors.translations
                                  ? Object.keys(chapterErrors.translations)[0]
                                  : null
                                if (firstErrorLang) {
                                  setActiveLanguageTab(firstErrorLang as LanguageCode)
                                }
                                // Scroll to chapter section
                                setTimeout(() => {
                                  const chapterSection =
                                    document.querySelector('[data-chapter-section]')
                                  chapterSection?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                  })
                                }, 100)
                              }}
                              className='text-sm font-semibold text-red-700 hover:text-red-900 underline cursor-pointer'
                            >
                              {t('common.chapterNumber', { number: chapterOrder })}:
                            </button>
                            {chapterErrors.translations &&
                              Object.keys(chapterErrors.translations).length > 0 && (
                                <ul className='list-disc list-inside ml-4 space-y-1'>
                                  {Object.entries(chapterErrors.translations).map(
                                    ([lang, errors]) => {
                                      const language = LANGUAGES.find(l => l.code === lang)
                                      return (
                                        <li key={lang} className='text-sm text-red-600 '>
                                          <span className='font-medium'>
                                            {language?.flag} {language?.name}:
                                          </span>{' '}
                                          {errors.title}
                                        </li>
                                      )
                                    }
                                  )}
                                </ul>
                              )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Chapter Question Issues */}
                {formErrors.chapterQuestionIssues &&
                  Object.keys(formErrors.chapterQuestionIssues).length > 0 && (
                    <div className='p-3 bg-red-100 rounded border border-red-300 '>
                      <p className='text-sm font-medium text-red-700 mb-2'>
                        {t('common.quizQuestionIssues')}
                      </p>
                      {Object.entries(formErrors.chapterQuestionIssues).map(([cIdx, langMap]) => {
                        const chapterIndex = parseInt(cIdx, 10)
                        const chapter = chapters[chapterIndex]
                        const chapterOrder = chapter?.order || chapterIndex + 1
                        return (
                          <div key={cIdx} className='mb-2 ml-2'>
                            <button
                              type='button'
                              onClick={() => {
                                setActiveChapterIndex(chapterIndex)
                                // Switch to the first language with issues if available
                                const firstErrorLang = Object.keys(langMap)[0]
                                if (firstErrorLang) {
                                  setActiveLanguageTab(firstErrorLang as LanguageCode)
                                }
                                // Scroll to chapter section
                                setTimeout(() => {
                                  const chapterSection =
                                    document.querySelector('[data-chapter-section]')
                                  chapterSection?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                  })
                                }, 100)
                              }}
                              className='text-xs font-semibold text-red-700 hover:text-red-900 underline cursor-pointer'
                            >
                              {t('common.chapterNumber', { number: chapterOrder })}:
                            </button>
                            {Object.entries(langMap).map(([lang, issues]) => {
                              const language = LANGUAGES.find(l => l.code === lang)
                              return (
                                <div key={lang} className='ml-2'>
                                  <p className='text-xs font-semibold text-red-600 '>
                                    {language?.flag} {language?.name}:
                                  </p>
                                  <ul className='list-disc ml-4 text-xs text-red-600 '>
                                    {issues.map((issue, idx) => (
                                      <li key={idx}>{issue}</li>
                                    ))}
                                  </ul>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })}
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Draft / publish status when editing */}
          {isEditing && !formData.isActive && (
            <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
              <p className='text-sm text-amber-800'>{t('common.draftNotPublishedHint', typeI18n)}</p>
            </div>
          )}

          {/* Grundinformationen + Modultitel & Beschreibung */}
          <div className='highlight-card rounded-[var(--radius-xl)] p-8'>
            <h2 className='text-xl font-semibold mb-6 text-neutral-900 '>
              {t('common.basicInformation')}
            </h2>

            <div className='space-y-6'>
              {isEditing && (
                <div className='flex items-center justify-between gap-4 flex-wrap'>
                  <div className='flex items-center'>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                        formData.isActive
                          ? 'bg-green-50 text-green-800 border-green-200'
                          : 'bg-amber-50 text-amber-800 border-amber-200'
                      }`}
                    >
                      {formData.isActive ? t('common.active') : t('common.draft')}
                    </span>
                    <span className='ml-3 text-sm text-neutral-600'>
                      {formData.isActive
                        ? t('common.isActiveLabel', { type: typeLabel })
                        : t('common.draftStatusLabel', { type: typeLabel })}
                    </span>
                  </div>
                </div>
              )}

              {/* Single language control: chips = tabs + add */}
              <div>
                  <label className='block text-sm font-medium text-neutral-700 mb-2'>
                    {t('common.languages')}
                  </label>
                  <div className='flex flex-wrap items-center gap-2'>
                    {selectedLanguages.map(langCode => {
                      const language = LANGUAGES.find(l => l.code === langCode)
                      const isDefault = langCode === formData.defaultLanguage
                      const isActive = langCode === activeLanguageTab
                      return (
                        <button
                          key={langCode}
                          type='button'
                          onClick={() => setActiveLanguageTab(langCode)}
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            isActive
                              ? 'btn-primary-modern text-white border-transparent shadow-sm'
                              : 'bg-primary-50 text-primary-800 border-primary-200 hover:bg-primary-100'
                          }`}
                        >
                          {language?.flag} {language?.name}
                          {isDefault && (
                            <span
                              className={`ml-1.5 text-xs ${isActive ? 'opacity-80' : 'opacity-70'}`}
                            >
                              ({t('common.default')})
                            </span>
                          )}
                          {!isDefault && (
                            <span
                              role='button'
                              tabIndex={0}
                              onClick={e => {
                                e.stopPropagation()
                                handleLanguageToggle(langCode)
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleLanguageToggle(langCode)
                                }
                              }}
                              className={`ml-2 ${
                                isActive
                                  ? 'text-white/80 hover:text-white'
                                  : 'text-primary-600 hover:text-primary-700'
                              }`}
                              aria-label={t('common.removeLanguage')}
                            >
                              ×
                            </span>
                          )}
                        </button>
                      )
                    })}

                    {languagesAvailableToAdd.length > 0 &&
                      (!showLanguagePicker ? (
                        <button
                          type='button'
                          onClick={() => setShowLanguagePicker(true)}
                          className='btn-primary-modern w-8 h-8 rounded-full font-medium inline-flex items-center justify-center'
                          aria-label={t('common.addLanguage')}
                          title={t('common.addLanguage')}
                        >
                          <svg
                            className='w-4 h-4'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 4v16m8-8H4'
                            />
                          </svg>
                        </button>
                      ) : (
                        <>
                          <select
                            autoFocus
                            defaultValue=''
                            onChange={e => {
                              if (e.target.value) {
                                handleAddLanguage(e.target.value as LanguageCode)
                              }
                            }}
                            className='input-field max-w-xs'
                            aria-label={t('common.addLanguage')}
                          >
                            <option value='' disabled>
                              {t('common.addLanguagePlaceholder')}
                            </option>
                            {languagesAvailableToAdd.map(language => (
                              <option key={language.code} value={language.code}>
                                {language.flag} {language.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type='button'
                            onClick={() => setShowLanguagePicker(false)}
                            className='btn-secondary-modern px-3 py-1.5 rounded-lg text-sm font-medium'
                          >
                            {t('common.cancel')}
                          </button>
                        </>
                      ))}
                  </div>
                  {activeLanguageTab !== formData.defaultLanguage && (
                    <button
                      type='button'
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          defaultLanguage: activeLanguageTab,
                        }))
                      }
                      className='mt-2 text-xs text-primary-700 hover:text-primary-900 underline'
                    >
                      {t('common.setAsDefaultLanguage')}
                    </button>
                  )}
                  {formErrors.languageSelection && (
                    <p className='mt-2 text-xs text-red-600'>{formErrors.languageSelection}</p>
                  )}
                </div>

              {/* Modultitel & Beschreibung — same language pills above act as tabs */}
              {selectedLanguages.length > 0 && (
                <div>
                  <h3 className='text-lg font-semibold mb-4 text-neutral-900'>
                    {t('common.moduleTitleAndDescription', typeI18n)}
                  </h3>

                  {formData.translations[activeLanguageTab] && (
                    <div className='space-y-6'>
                      <div className='grid grid-cols-1 gap-6'>
                        <div>
                          <label className='block text-sm font-medium text-neutral-700 mb-2'>
                            {t('common.moduleTitle', typeI18n)}
                          </label>
                          <input
                            type='text'
                            value={formData.translations[activeLanguageTab].title}
                            onChange={e =>
                              handleTranslationChange(activeLanguageTab, 'title', e.target.value)
                            }
                            required
                            className={`input-field w-full ${formErrors.translations?.[activeLanguageTab]?.title ? '!border-error-500' : ''}`}
                            placeholder={t('modules.moduleTitlePlaceholder')}
                          />
                          {formErrors.translations?.[activeLanguageTab]?.title && (
                            <p className='mt-1 text-xs text-red-600 '>
                              {formErrors.translations[activeLanguageTab]!.title}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className='block text-sm font-medium text-neutral-700 mb-2'>
                            {t('common.description')}
                          </label>
                          <textarea
                            value={formData.translations[activeLanguageTab].description}
                            onChange={e =>
                              handleTranslationChange(
                                activeLanguageTab,
                                'description',
                                e.target.value
                              )
                            }
                            rows={4}
                            className='input-field w-full'
                            placeholder={t('modules.moduleDescriptionPlaceholder')}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Chapters — only after first save (edit mode) */}
          {showChapters && (
          <div className='highlight-card rounded-[var(--radius-xl)] p-8' data-chapter-section>
            <div className='flex items-center justify-between mb-6'>
              <div>
                <h2 className='text-xl font-semibold text-neutral-900 '>{t('common.chapters')}</h2>
                <p className='text-sm text-neutral-600 '>{t('common.chaptersHint')}</p>
              </div>
              <button
                type='button'
                onClick={() => {
                  // Initialize translations/questions for selected languages
                  const initTranslations = Object.fromEntries(
                    selectedLanguages.map(l => [l, { title: '', description: '' }])
                  ) as ChapterForm['translations']
                  const initQuestions = Object.fromEntries(
                    selectedLanguages.map(l => [l, [] as QuizQuestion[]])
                  ) as ChapterForm['questions']
                  setChapters(prev => [
                    ...prev,
                    {
                      order: prev.length + 1,
                      videoUrl: '',
                      imageUrl: '',
                      duration: undefined,
                      translations: initTranslations,
                      questions: initQuestions,
                    },
                  ])
                  setActiveChapterIndex(chapters.length)
                }}
                className='btn-primary-modern px-4 py-2 rounded-lg font-medium flex items-center space-x-2'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 4v16m8-8H4'
                  />
                </svg>
                <span>{t('common.addChapter')}</span>
              </button>
            </div>

            {chapters.length === 0 ? (
              <p className='text-neutral-600 '>{t('common.noChaptersYet')}</p>
            ) : (
              <div className='space-y-6'>
                {/* Chapter Tabs */}
                <div className='flex flex-wrap gap-2'>
                  {chapters.map((ch, idx) => (
                    <button
                      key={idx}
                      type='button'
                      onClick={() => setActiveChapterIndex(idx)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        activeChapterIndex === idx
                          ? 'btn-primary-modern text-white shadow-sm'
                          : 'bg-primary-50 text-primary-800 border border-primary-200 hover:bg-primary-100 '
                      }`}
                    >
                      {t('common.chapterNumber', { number: ch.order || idx + 1 })}
                    </button>
                  ))}
                </div>

                {/* Active Chapter Editor */}
                {activeChapterIndex >= 0 && chapters[activeChapterIndex] && (
                  <div className='highlight-card rounded-xl p-6'>
                    <div className='flex items-center justify-between mb-4'>
                      <h3 className='text-lg font-semibold text-neutral-900 '>
                        {t('common.chapterNumber', {
                          number: chapters[activeChapterIndex].order || activeChapterIndex + 1,
                        })}
                      </h3>
                      <div className='flex items-center gap-2'>
                        <button
                          type='button'
                          onClick={() => {
                            setChapters(prev => {
                              const next = prev.slice()
                              next.splice(activeChapterIndex, 1)
                              // re-order remaining
                              return next.map((c, i) => ({ ...c, order: i + 1 }))
                            })
                            setActiveChapterIndex(prev => Math.max(0, prev - 1))
                          }}
                          className='px-3 py-1 text-sm rounded-lg border border-red-300 text-red-600 hover:bg-red-50 '
                        >
                          {t('common.removeChapter')}
                        </button>
                      </div>
                    </div>

                    {/* Chapter basic fields */}
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
                      <div>
                        <label className='block text-sm font-medium text-neutral-700 mb-2'>
                          {t('common.order')}
                        </label>
                        <input
                          type='number'
                          min={1}
                          value={chapters[activeChapterIndex].order}
                          onChange={e => {
                            const val = Math.max(1, parseInt(e.target.value || '1', 10))
                            setChapters(prev =>
                              prev.map((c, i) =>
                                i === activeChapterIndex ? { ...c, order: val } : c
                              )
                            )
                          }}
                          className='input-field w-full'
                        />
                      </div>
                      <div className='md:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-4'>
                        <div>
                          <label className='block text-sm font-medium text-neutral-700 mb-2'>
                            {t('common.uploadVideo')}
                          </label>
                          <div className='space-y-2'>
                            <input
                              type='file'
                              accept='video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo'
                              onChange={e => {
                                const file = e.target.files?.[0]
                                if (file) {
                                  handleVideoUpload(file, activeChapterIndex)
                                }
                              }}
                              disabled={
                                uploadingVideo === activeChapterIndex ||
                                uploadingImage === activeChapterIndex
                              }
                              className='input-field w-full disabled:opacity-50 disabled:cursor-not-allowed'
                            />
                            {uploadingVideo === activeChapterIndex && (
                              <p className='text-sm text-primary-600 '>{t('common.uploadingVideo')}</p>
                            )}
                            {chapters[activeChapterIndex].videoUrl && (
                              <div className='mt-2'>
                                <p className='text-sm text-neutral-600 mb-1'>
                                  {t('common.currentVideo')}
                                </p>
                                <a
                                  href={chapters[activeChapterIndex].videoUrl}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-sm text-primary-600 hover:underline break-all'
                                >
                                  {chapters[activeChapterIndex].videoUrl}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className='block text-sm font-medium text-neutral-700 mb-2'>
                            {t('common.uploadImage')}
                          </label>
                          <div className='space-y-2'>
                            <input
                              type='file'
                              accept='image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif'
                              onChange={e => {
                                const file = e.target.files?.[0]
                                if (file) {
                                  handleImageUpload(file, activeChapterIndex)
                                }
                              }}
                              disabled={
                                uploadingImage === activeChapterIndex ||
                                uploadingVideo === activeChapterIndex
                              }
                              className='input-field w-full disabled:opacity-50 disabled:cursor-not-allowed'
                            />
                            {uploadingImage === activeChapterIndex && (
                              <p className='text-sm text-primary-600 '>{t('common.uploadingImage')}</p>
                            )}
                            {chapters[activeChapterIndex].imageUrl && (
                              <div className='mt-2'>
                                <p className='text-sm text-neutral-600 mb-1'>
                                  {t('common.currentImage')}
                                </p>
                                <a
                                  href={chapters[activeChapterIndex].imageUrl}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-sm text-primary-600 hover:underline break-all'
                                >
                                  {chapters[activeChapterIndex].imageUrl}
                                </a>
                                <img
                                  src={chapters[activeChapterIndex].imageUrl}
                                  alt={t('common.chapterImagePreview')}
                                  className='mt-2 h-28 rounded-xl border border-primary-100 object-cover shadow-sm'
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Chapter language tabs */}
                    <div className='flex flex-wrap gap-2 mb-4'>
                      {selectedLanguages.map(lang => (
                        <button
                          key={lang}
                          type='button'
                          onClick={() => setActiveLanguageTab(lang)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium ${
                            activeLanguageTab === lang
                              ? 'btn-primary-modern text-white shadow-sm'
                              : 'bg-primary-50 text-primary-800 border border-primary-200 hover:bg-primary-100 '
                          }`}
                        >
                          {LANGUAGES.find(l => l.code === lang)?.flag}{' '}
                          {LANGUAGES.find(l => l.code === lang)?.name}
                        </button>
                      ))}
                    </div>

                    {/* Chapter translations for active language */}
                    <div className='grid grid-cols-1 gap-4 mb-6'>
                      <div>
                        <label className='block text-sm font-medium text-neutral-700 mb-2'>
                          {t('common.chapterTitle')}
                        </label>
                        <input
                          type='text'
                          value={
                            chapters[activeChapterIndex].translations[activeLanguageTab]?.title ||
                            ''
                          }
                          onChange={e =>
                            handleChapterTitleChange(
                              activeChapterIndex,
                              activeLanguageTab,
                              e.target.value
                            )
                          }
                          className={`input-field w-full ${formErrors.chapters?.[activeChapterIndex]?.translations?.[activeLanguageTab]?.title ? '!border-error-500' : ''}`}
                        />
                        {formErrors.chapters?.[activeChapterIndex]?.translations?.[
                          activeLanguageTab
                        ]?.title && (
                          <p className='mt-1 text-xs text-red-600 '>
                            {
                              formErrors.chapters[activeChapterIndex]!.translations![
                                activeLanguageTab
                              ]!.title
                            }
                          </p>
                        )}
                      </div>
                      <div>
                        <label className='block text-sm font-medium text-neutral-700 mb-2'>
                          {t('common.description')}
                        </label>
                        <textarea
                          rows={3}
                          value={
                            chapters[activeChapterIndex].translations[activeLanguageTab]
                              ?.description || ''
                          }
                          onChange={e =>
                            setChapters(prev =>
                              prev.map((c, i) =>
                                i === activeChapterIndex
                                  ? {
                                      ...c,
                                      translations: {
                                        ...c.translations,
                                        [activeLanguageTab]: {
                                          ...(c.translations[activeLanguageTab] || {
                                            title: '',
                                            description: '',
                                          }),
                                          description: e.target.value,
                                        },
                                      },
                                    }
                                  : c
                              )
                            )
                          }
                          className='input-field w-full'
                        />
                      </div>
                    </div>

                    {/* Chapter quiz questions for active language */}
                    <div className='mt-4'>
                      <div className='flex items-center justify-between mb-3'>
                        <h4 className='text-md font-semibold text-neutral-900 '>
                          {t('common.quizQuestions', { lang: activeLanguageTab.toUpperCase() })}
                        </h4>
                        <button
                          type='button'
                          onClick={() => {
                            // reuse existing newQuestion state
                            setNewQuestion({
                              question: '',
                              options: ['', ''],
                              correctAnswer: 0,
                              explanation: '',
                            })
                            setShowQuestionForm(true)
                            setEditingQuestionIndex(null)
                          }}
                          className='btn-primary-modern px-3 py-1 rounded-lg text-sm'
                        >
                          {t('common.addQuestion')}
                        </button>
                      </div>

                      {/* List of questions */}
                      {(chapters[activeChapterIndex].questions[activeLanguageTab] || []).map(
                        (q, qi) => (
                          <div
                            key={qi}
                            className='mb-3 p-3 rounded-xl border border-primary-100 bg-white/70 shadow-sm '
                          >
                            <div className='flex items-center justify-between'>
                              <div className='font-medium text-neutral-900 '>
                                {t('common.questionWithText', { number: qi + 1, text: q.question })}
                              </div>
                              <div className='flex gap-2'>
                                <button
                                  type='button'
                                  className='text-xs px-2 py-1 rounded-lg bg-primary-50 text-primary-800 border border-primary-100 hover:bg-primary-100 '
                                  onClick={() => {
                                    setNewQuestion({ ...q })
                                    setEditingQuestionIndex(qi)
                                    setShowQuestionForm(true)
                                  }}
                                >
                                  {t('common.edit')}
                                </button>
                                <button
                                  type='button'
                                  className='text-xs px-2 py-1 rounded bg-red-100 text-red-700 '
                                  onClick={() =>
                                    setChapters(prev =>
                                      prev.map((c, i) =>
                                        i === activeChapterIndex
                                          ? {
                                              ...c,
                                              questions: {
                                                ...c.questions,
                                                [activeLanguageTab]: (
                                                  c.questions[activeLanguageTab] || []
                                                ).filter((_, idx) => idx !== qi),
                                              },
                                            }
                                          : c
                                      )
                                    )
                                  }
                                >
                                  {t('common.delete')}
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      )}

                      {/* New/Edit question modal inline */}
                      {showQuestionForm && (
                        <div className='mt-4 p-4 rounded-xl border border-primary-200 bg-primary-50/80 shadow-sm '>
                          {/* Reuse existing fields from lower section */}
                          {/* We'll render a smaller variant and commit into chapter state */}
                          {/* Question */}
                          <div className='mb-3'>
                            <label className='block text-sm font-medium text-neutral-700 mb-1'>
                              {t('common.question')} *
                            </label>
                            <textarea
                              value={newQuestion.question}
                              onChange={e => handleNewQuestionChange('question', e.target.value)}
                              rows={2}
                              className='input-field w-full'
                              placeholder={t('modules.questionPlaceholder')}
                            />
                          </div>

                          {/* Options */}
                          <div className='mb-3'>
                            <div className='flex items-center justify-between mb-2'>
                              <label className='block text-sm font-medium text-neutral-700 '>
                                {t('common.answerOptions')}
                              </label>
                              <button
                                type='button'
                                onClick={handleAddOption}
                                className='text-primary-700 hover:text-primary-800 text-xs font-medium'
                              >
                                {t('common.addOption')}
                              </button>
                            </div>
                            <div className='space-y-2'>
                              {newQuestion.options.map((opt, oi) => (
                                <div key={oi} className='flex items-center gap-2'>
                                  <input
                                    type='radio'
                                    name='chapterCorrectAnswer'
                                    checked={newQuestion.correctAnswer === oi}
                                    onChange={() => handleNewQuestionChange('correctAnswer', oi)}
                                  />
                                  <input
                                    type='text'
                                    value={opt}
                                    onChange={e =>
                                      handleNewQuestionOptionChange(oi, e.target.value)
                                    }
                                    className='input-field flex-1 py-1'
                                  />
                                  {newQuestion.options.length > 2 && (
                                    <button
                                      type='button'
                                      onClick={() => handleRemoveOption(oi)}
                                      className='text-xs text-red-600'
                                    >
                                      {t('common.remove')}
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className='mb-4'>
                            <label className='block text-sm font-medium text-neutral-700 mb-1'>
                              {t('common.explanation')}
                            </label>
                            <textarea
                              rows={2}
                              value={newQuestion.explanation}
                              onChange={e => handleNewQuestionChange('explanation', e.target.value)}
                              className='input-field w-full'
                            />
                          </div>

                          <div className='flex justify-end gap-2'>
                            <button
                              type='button'
                              onClick={() => {
                                setShowQuestionForm(false)
                                setEditingQuestionIndex(null)
                                setNewQuestion({
                                  question: '',
                                  options: ['', ''],
                                  correctAnswer: 0,
                                  explanation: '',
                                })
                              }}
                              className='btn-secondary-modern px-3 py-1 rounded-lg'
                            >
                              {t('common.cancel')}
                            </button>
                            <button
                              type='button'
                              className='btn-primary-modern px-3 py-1 rounded-lg'
                              onClick={() => {
                                const filledOptions = newQuestion.options.filter(o => o.trim())
                                if (
                                  !newQuestion.question.trim() ||
                                  filledOptions.length < 2 ||
                                  newQuestion.correctAnswer >= filledOptions.length
                                ) {
                                  alert(t('errors.questionValidationAlert'))
                                  return
                                }
                                const q = {
                                  ...newQuestion,
                                  options: filledOptions,
                                  explanation: newQuestion.explanation?.trim() || undefined,
                                }
                                setChapters(prev =>
                                  prev.map((c, i) => {
                                    if (i !== activeChapterIndex) return c
                                    const list = c.questions[activeLanguageTab] || []
                                    const next =
                                      editingQuestionIndex !== null
                                        ? list.map((qq, idx) =>
                                            idx === editingQuestionIndex ? q : qq
                                          )
                                        : [...list, q]
                                    return {
                                      ...c,
                                      questions: { ...c.questions, [activeLanguageTab]: next },
                                    }
                                  })
                                )
                                setShowQuestionForm(false)
                                setEditingQuestionIndex(null)
                                setNewQuestion({
                                  question: '',
                                  options: ['', ''],
                                  correctAnswer: 0,
                                  explanation: '',
                                })
                              }}
                            >
                              {t('common.save')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Submit Buttons */}
          <div className='flex gap-4 pt-6 border-t border-primary-100 flex-col'>
            <div className='flex items-center justify-between gap-3 flex-wrap'>
              {isEditing && (
                <button
                  type='button'
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleteModuleMutation.isPending}
                  className='px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-neutral-400 text-white rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2'
                >
                  <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                    />
                  </svg>
                  <span>{t('common.deleteModule', typeI18n)}</span>
                </button>
              )}

              <div className='flex items-center gap-3 ml-auto flex-wrap'>
                {/* Create: Speichern (draft) */}
                {!isEditing && (
                  <button
                    type='submit'
                    disabled={isBusy}
                    className='btn-primary-modern disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium flex items-center space-x-2'
                  >
                    {isBusy ? (
                      <>
                        <svg className='animate-spin w-4 h-4' fill='none' viewBox='0 0 24 24'>
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                          />
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          />
                        </svg>
                        <span>{t('common.creating')}</span>
                      </>
                    ) : (
                      <span>{t('common.save')}</span>
                    )}
                  </button>
                )}

                {/* Edit: Speichern (keep current active state) */}
                {isEditing && (
                  <button
                    type='button'
                    disabled={isBusy}
                    onClick={() => handleSubmit(undefined, { activate: false })}
                    className={`${
                      formData.isActive ? 'btn-primary-modern text-white' : 'btn-secondary-modern'
                    } disabled:opacity-60 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-medium`}
                  >
                    {isBusy ? t('common.savingChanges') : t('common.saveChanges')}
                  </button>
                )}

                {/* Edit inactive: Jetzt freigeben */}
                {isEditing && !formData.isActive && (
                  <button
                    type='button'
                    disabled={isBusy}
                    onClick={() => handleSubmit(undefined, { activate: true })}
                    className='btn-primary-modern disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium flex items-center space-x-2'
                  >
                    {isBusy ? (
                      <>
                        <svg className='animate-spin w-4 h-4' fill='none' viewBox='0 0 24 24'>
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                          />
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          />
                        </svg>
                        <span>{t('common.publishing')}</span>
                      </>
                    ) : (
                      <span>{t('common.publishNow')}</span>
                    )}
                  </button>
                )}
              </div>
            </div>
            {error && !loading && <p className='text-sm text-red-600 mt-2'>{error}</p>}
            {!error &&
              !loading &&
              (createModuleMutation.isSuccess || updateModuleMutation.isSuccess) && (
                <p className='text-sm text-green-600 mt-2'>{t('common.savedSuccessfully')}</p>
              )}
          </div>
        </form>
      </Container>
    </PageLayout>
  )
}
